export type HudState = {
  hp?: number;
  hunger?: number;
  thirst?: number;
  tod?: string;
  todMinutes?: number;
  compass?: string;
  yaw?: number;
  targetYaw?: number;
  reticle?: boolean;
  blind?: number;
  notice?: string;
  bearing?: string;
  bearingSide?: 'ahead' | 'left' | 'right' | 'behind';
  hurt?: number;
  build?: string;
  swing?: boolean;
};

type MeterId = 'hp' | 'hunger' | 'thirst';

const TOD_MIN_START = 16 * 60 + 10;

const DUMMY: {
  hp: number;
  hunger: number;
  thirst: number;
  todMinutes: number;
  compass: string;
} = {
  hp: 100,
  hunger: 72,
  thirst: 64,
  todMinutes: TOD_MIN_START,
  compass: 'N',
};

const METER_LABEL: Record<MeterId, string> = {
  hp: 'Health',
  hunger: 'Hunger',
  thirst: 'Thirst',
};

let instance: Hud | null = null;

export class Hud {
  private root: HTMLElement | null = null;
  private shell: HTMLElement | null = null;
  private todEl: HTMLElement | null = null;
  private compassEl: HTMLElement | null = null;
  private reticleEl: HTMLElement | null = null;
  private splashWatch: MutationObserver | null = null;
  private live = false;
  private reticleOn = true;
  private blind = 0;
  private notice = '';
  private bearing = '';
  private bearingSide: 'ahead' | 'left' | 'right' | 'behind' | '' = '';
  private hurt = 0;
  private build = '';
  private swing = false;
  private blindEl: HTMLElement | null = null;
  private noticeEl: HTMLElement | null = null;
  private bearingEl: HTMLElement | null = null;
  private hurtEl: HTMLElement | null = null;
  private buildEl: HTMLElement | null = null;
  private hp = DUMMY.hp;
  private hunger = DUMMY.hunger;
  private thirst = DUMMY.thirst;
  private todMinutes = DUMMY.todMinutes;
  private todText = formatClock(DUMMY.todMinutes);
  private compass = DUMMY.compass;
  private yaw = 0;
  private targetYaw = 0;
  private hasBearing = false;
  private readonly fills: Partial<Record<MeterId, HTMLElement>> = {};
  private readonly values: Partial<Record<MeterId, HTMLElement>> = {};
  private readonly meters: Partial<Record<MeterId, HTMLElement>> = {};

  static mount(root?: HTMLElement | null): Hud {
    if (!instance) {
      instance = new Hud();
    }
    instance.attach(root);
    return instance;
  }

  static set(state: HudState): void {
    Hud.mount().apply(state);
  }

  static show(): void {
    Hud.mount().reveal();
  }

  private attach(root?: HTMLElement | null): void {
    const host = resolveRoot(root);
    if (!host) {
      throw new Error('Missing #ui-root');
    }
    if (this.root === host && this.shell) {
      this.watchSplash();
      return;
    }
    this.root = host;
    this.shell = this.ensureShell(host);
    this.cache(this.shell);
    this.paint();
    this.syncReticle();
    this.watchSplash();
  }

  private ensureShell(host: HTMLElement): HTMLElement {
    const existing = host.querySelector<HTMLElement>('#hud');
    if (existing) {
      return existing;
    }

    const shell = document.createElement('div');
    shell.id = 'hud';
    shell.hidden = true;
    shell.setAttribute('data-hud-shell', '');

    const watch = document.createElement('div');
    watch.className = 'hud-watch';

    const tod = document.createElement('div');
    tod.id = 'tod';
    tod.setAttribute('data-hud', 'tod');
    tod.setAttribute('aria-label', 'Time of day');

    const compass = document.createElement('div');
    compass.id = 'compass';
    compass.setAttribute('data-hud', 'compass');
    compass.setAttribute('aria-label', 'Compass');

    watch.append(tod, compass);

    const objective = document.createElement('div');
    objective.id = 'objective';
    objective.setAttribute('data-hud', 'objective');
    objective.setAttribute('aria-live', 'polite');
    objective.setAttribute('aria-label', 'Objective');

    const now = document.createElement('div');
    now.className = 'objective-now';
    now.textContent = 'Find the wreck spear';

    const done = document.createElement('ul');
    done.className = 'objective-done';
    done.hidden = true;

    objective.append(now, done);

    const rail = document.createElement('div');
    rail.className = 'hud-meters';
    rail.append(this.makeMeter('hp'), this.makeMeter('hunger'), this.makeMeter('thirst'));

    const prompt = document.createElement('div');
    prompt.id = 'prompt';
    prompt.setAttribute('data-hud', 'prompt');
    prompt.setAttribute('aria-live', 'polite');
    prompt.setAttribute('aria-label', 'Interact');
    prompt.hidden = true;

    const inv = document.createElement('div');
    inv.id = 'inv';
    inv.setAttribute('data-hud', 'inv');
    inv.setAttribute('aria-label', 'Inventory');
    inv.hidden = true;

    const hint = document.createElement('div');
    hint.id = 'controls-hint';
    hint.setAttribute('data-hud', 'controls');
    hint.setAttribute('aria-label', 'Controls');
    hint.textContent =
      'WASD · mouse · Shift · Space · C sneak · E use · LMB attack · B build · Q cycle · F fire';

    const bearing = document.createElement('div');
    bearing.id = 'hud-bearing';
    bearing.setAttribute('data-hud', 'bearing');
    bearing.setAttribute('aria-live', 'polite');

    const build = document.createElement('div');
    build.id = 'hud-build';
    build.setAttribute('data-hud', 'build');
    build.hidden = true;

    const notice = document.createElement('div');
    notice.id = 'hud-notice';
    notice.setAttribute('data-hud', 'notice');
    notice.setAttribute('aria-live', 'polite');
    notice.hidden = true;

    const hurt = document.createElement('div');
    hurt.id = 'hud-hurt';
    hurt.setAttribute('data-hud', 'hurt');
    hurt.setAttribute('aria-hidden', 'true');
    hurt.hidden = true;

    const blind = document.createElement('div');
    blind.id = 'hud-blind';
    blind.setAttribute('data-hud', 'blind');
    blind.setAttribute('aria-hidden', 'true');
    blind.hidden = true;

    shell.append(watch, objective, bearing, rail, prompt, inv, hint, build, notice, hurt, blind);
    host.append(shell);
    return shell;
  }

  private makeMeter(id: MeterId): HTMLElement {
    const row = document.createElement('div');
    row.id = id;
    row.className = `hud-meter hud-meter-${id}`;
    row.setAttribute('data-hud', id);
    row.setAttribute('role', 'meter');
    row.setAttribute('aria-label', METER_LABEL[id]);
    row.setAttribute('aria-valuemin', '0');
    row.setAttribute('aria-valuemax', '100');

    const label = document.createElement('span');
    label.className = 'hud-meter-label';
    label.textContent = METER_LABEL[id];

    const track = document.createElement('span');
    track.className = 'hud-meter-track';

    const fill = document.createElement('span');
    fill.className = 'hud-meter-fill';
    track.append(fill);

    const value = document.createElement('span');
    value.className = 'hud-meter-value';

    row.append(label, track, value);
    return row;
  }

  private cache(shell: HTMLElement): void {
    this.todEl = shell.querySelector<HTMLElement>('#tod');
    this.compassEl = shell.querySelector<HTMLElement>('#compass');
    this.reticleEl = shell.querySelector<HTMLElement>('#hud-reticle');
    this.blindEl = shell.querySelector<HTMLElement>('#hud-blind');
    this.noticeEl = shell.querySelector<HTMLElement>('#hud-notice');
    this.bearingEl = shell.querySelector<HTMLElement>('#hud-bearing');
    this.hurtEl = shell.querySelector<HTMLElement>('#hud-hurt');
    this.buildEl = shell.querySelector<HTMLElement>('#hud-build');
    for (const id of ['hp', 'hunger', 'thirst'] as const) {
      const row = shell.querySelector<HTMLElement>(`#${id}`);
      if (!row) {
        continue;
      }
      this.meters[id] = row;
      const fill = row.querySelector<HTMLElement>('.hud-meter-fill');
      const value = row.querySelector<HTMLElement>('.hud-meter-value');
      if (fill) {
        this.fills[id] = fill;
      }
      if (value) {
        this.values[id] = value;
      }
    }
  }

  apply(state: HudState): void {
    if (state.hp !== undefined) {
      this.hp = clampMeter(state.hp);
    }
    if (state.hunger !== undefined) {
      this.hunger = clampMeter(state.hunger);
    }
    if (state.thirst !== undefined) {
      this.thirst = clampMeter(state.thirst);
    }
    if (state.todMinutes !== undefined) {
      this.todMinutes = wrapMinutes(state.todMinutes);
      this.todText = formatClock(this.todMinutes);
    } else if (state.tod !== undefined) {
      this.todText = state.tod;
    }
    if (state.compass !== undefined) {
      this.compass = state.compass;
    }
    if (state.yaw !== undefined && Number.isFinite(state.yaw)) {
      this.yaw = state.yaw;
      this.hasBearing = true;
    }
    if (state.targetYaw !== undefined && Number.isFinite(state.targetYaw)) {
      this.targetYaw = state.targetYaw;
      this.hasBearing = true;
    }
    if (state.reticle !== undefined) {
      this.reticleOn = state.reticle;
    }
    if (state.blind !== undefined) {
      this.blind = Number.isFinite(state.blind) ? Math.max(0, state.blind) : 0;
    }
    if (state.notice !== undefined) {
      this.notice = state.notice;
    }
    if (state.bearing !== undefined) {
      this.bearing = state.bearing;
    }
    if (state.bearingSide !== undefined) {
      this.bearingSide = state.bearingSide;
    }
    if (state.hurt !== undefined) {
      this.hurt = Number.isFinite(state.hurt) ? Math.max(0, state.hurt) : 0;
    }
    if (state.build !== undefined) {
      this.build = state.build;
    }
    if (state.swing !== undefined) {
      this.swing = state.swing;
    }
    this.paint();
    this.syncReticle();
    this.syncBlind();
    this.syncNotice();
    this.syncBearing();
    this.syncHurt();
    this.syncBuild();
    this.syncSwing();
  }

  private paint(): void {
    this.writeMeter('hp', this.hp);
    this.writeMeter('hunger', this.hunger);
    this.writeMeter('thirst', this.thirst);
    if (this.todEl) {
      this.todEl.textContent = this.todText;
    }
    if (this.compassEl) {
      this.paintCompass(this.compassEl);
    }
  }

  private paintCompass(el: HTMLElement): void {
    el.style.transform = '';
    if (!this.hasBearing) {
      el.textContent = this.compass;
      el.setAttribute('aria-label', `Compass ${this.compass}`);
      return;
    }
    const two = Math.PI * 2;
    const heading = ((-this.yaw % two) + two) % two;
    const card = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(heading / (Math.PI / 4)) % 8] ?? 'N';
    const turn = Math.round((((this.targetYaw - this.yaw) * 180) / Math.PI + 540) % 360 - 180);
    el.textContent = `${card} ${this.bearingSide || ''}`.trim();
    el.setAttribute('data-turn', String(turn));
    el.setAttribute('aria-label', this.bearing || `Facing ${card}`);
  }

  private writeMeter(id: MeterId, amount: number): void {
    const row = this.meters[id];
    const fill = this.fills[id];
    const value = this.values[id];
    if (row) {
      row.setAttribute('aria-valuenow', String(amount));
    }
    if (fill) {
      fill.style.width = `${amount}%`;
    }
    if (value) {
      value.textContent = String(Math.round(amount));
    }
  }

  private syncNotice(): void {
    const el = this.noticeEl;
    if (!el) {
      return;
    }
    el.textContent = this.notice;
    el.hidden = this.notice.length === 0;
  }

  private syncBearing(): void {
    const el = this.bearingEl;
    if (!el) {
      return;
    }
    el.textContent = this.bearing;
    el.hidden = this.bearing.length === 0;
    if (this.bearingSide) {
      el.setAttribute('data-side', this.bearingSide);
    }
  }

  private syncHurt(): void {
    const el = this.hurtEl;
    if (!el) {
      return;
    }
    const on = this.hurt > 0;
    el.hidden = !on;
    el.style.opacity = on ? String(Math.min(0.85, 0.25 + this.hurt / 40)) : '0';
  }

  private syncBuild(): void {
    const el = this.buildEl;
    if (!el) {
      return;
    }
    el.textContent = this.build;
    el.hidden = this.build.length === 0;
  }

  private syncSwing(): void {
    const mark = this.reticleEl;
    if (!mark) {
      return;
    }
    if (this.swing) {
      mark.setAttribute('data-swing', '');
    } else {
      mark.removeAttribute('data-swing');
    }
  }

  private syncBlind(): void {
    const veil = this.blindEl;
    if (!veil) {
      return;
    }
    const on = this.blind > 0;
    veil.hidden = !on;
    if (on) {
      veil.setAttribute('data-on', '');
      veil.style.opacity = String(Math.min(1, 0.35 + this.blind / 8));
    } else {
      veil.removeAttribute('data-on');
      veil.style.opacity = '0';
    }
  }

  private syncReticle(): void {
    if (!this.shell) {
      return;
    }
    if (!this.reticleOn) {
      this.reticleEl?.remove();
      this.reticleEl = null;
      return;
    }
    if (!this.reticleEl) {
      const mark = document.createElement('div');
      mark.id = 'hud-reticle';
      mark.className = 'hud-reticle';
      mark.setAttribute('data-hud', 'reticle');
      mark.setAttribute('aria-hidden', 'true');
      this.shell.append(mark);
      this.reticleEl = mark;
    }
  }

  private watchSplash(): void {
    const splash = document.getElementById('boot-splash');
    if (!splash || splash.classList.contains('hidden')) {
      this.reveal();
      return;
    }
    if (!this.splashWatch) {
      this.splashWatch = new MutationObserver(() => {
        if (splash.classList.contains('hidden')) {
          this.reveal();
        }
      });
      this.splashWatch.observe(splash, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden'],
      });
    }
    const start = document.getElementById('boot-start');
    start?.addEventListener('click', this.onSplashDismiss, { once: true });
  }

  private readonly onSplashDismiss = (): void => {
    this.reveal();
  };

  reveal(): void {
    if (this.live && this.shell && !this.shell.hidden) {
      return;
    }
    this.live = true;
    if (this.shell) {
      this.shell.hidden = false;
    }
    this.splashWatch?.disconnect();
    this.splashWatch = null;
  }
}

export function mountHud(root?: HTMLElement | null): Hud {
  return Hud.mount(root);
}

function resolveRoot(root?: HTMLElement | null): HTMLElement | null {
  if (root) {
    return root;
  }
  return (
    document.getElementById('ui-root') ??
    document.querySelector<HTMLElement>('[data-hud]')
  );
}

function clampMeter(n: number): number {
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(100, n));
}

function wrapMinutes(n: number): number {
  if (!Number.isFinite(n)) {
    return TOD_MIN_START;
  }
  const day = 24 * 60;
  return ((Math.floor(n) % day) + day) % day;
}

function formatClock(minutes: number): string {
  const wrapped = wrapMinutes(minutes);
  const hours = Math.floor(wrapped / 60);
  const mins = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}
